import {Card, CardBody} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useParams} from '@tanstack/react-router'
import {useState} from "react";
import PButton from "../components/PButton.tsx";
import PInput from "../components/PInput.tsx";
import services from "../services";

export const Route = createFileRoute('/race/$handle')({
    component: RouteComponent,
})

// The runner-facing page for a race: sign up beforehand, check in on the morning, and see the
// results afterwards. Public on purpose — no account needed to enter a local 10K.
function RouteComponent() {
    const {handle} = useParams({strict: false}) as {handle: string};

    const raceQuery = useQuery({
        queryKey: ['public-race', handle],
        queryFn:  () => services.races.publicRace(handle),
    });

    const [busy, setBusy] = useState(false);
    const [checkInValue, setCheckInValue] = useState('');
    const [checkedIn, setCheckedIn] = useState<{name: string; bib?: string} | null>(null);

    const race = raceQuery.data?.race;
    const results = raceQuery.data?.results || [];

    async function submitCheckIn() {
        if (!checkInValue.trim()) {
            addToast({color: 'danger', title: 'Enter the email you entered with, or your race number.'});
            return;
        }
        setBusy(true);
        try {
            // A runner might know either — send whichever it looks like.
            const isEmail = checkInValue.includes('@');
            const resp = await services.races.selfCheckIn(handle, isEmail ? {email: checkInValue.trim()} : {bib: checkInValue.trim()});
            if (resp?.checked_in) {
                setCheckedIn({name: resp.name, bib: resp.bib});
            } else {
                addToast({color: 'danger', title: resp?.message || "We couldn't find your entry."});
            }
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || "We couldn't find your entry for this race."});
        }
        setBusy(false);
    }

    if (raceQuery.isLoading) {
        return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;
    }
    if (!race) {
        return <div className="py-16 text-center text-default-500">Race not found.</div>;
    }

    const hasResults = results.length > 0;

    return (
        <div className="min-h-screen bg-[#EDEEF4] dark:bg-neutral-900">
            <title>{`${race.title} | ScorePal`}</title>

            <div className="bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] px-4 py-10">
                <div className="mx-auto max-w-3xl text-white">
                    <h1 className="font-bebas text-[clamp(30px,5vw,52px)] font-bold uppercase leading-none tracking-wide">{race.title}</h1>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-white/80">
                        {race.distance && <span className="flex items-center gap-1"><Icon icon="material-symbols-light:route" className="text-lg" />{race.distance}</span>}
                        {race.starts_at && <span className="flex items-center gap-1"><Icon icon="material-symbols-light:calendar-month-outline" className="text-lg" />{new Date(race.starts_at).toLocaleString()}</span>}
                        {race.venue && <span className="flex items-center gap-1"><Icon icon="material-symbols-light:location-on-outline" className="text-lg" />{race.venue}{race.city ? `, ${race.city}` : ''}</span>}
                    </div>
                    {race.description && <p className="mt-4 max-w-2xl text-white/85">{race.description}</p>}
                </div>
            </div>

            <div className="mx-auto max-w-3xl space-y-4 px-4 py-8">

                {/* Sign-up goes through the standard event registration flow — entry fees,
                    registration questions and waivers, same as every other ScorePal event. */}
                {race.registration_open && (
                    <Card>
                        <CardBody className="space-y-3">
                            <div>
                                <h2 className="text-lg font-bold">Enter this race</h2>
                                <p className="text-sm text-default-500">
                                    {race.entry_limit
                                        ? `${race.entries_count} of ${race.entry_limit} places taken.`
                                        : `${race.entries_count} entered so far.`}
                                    {race.entry_amount > 0 && ` Entry ${(race.entry_amount / 100).toFixed(2)} ${(race.entry_currency_code || 'USD').toUpperCase()}.`}
                                </p>
                            </div>
                            <PButton color="primary" size="lg"
                                     onPress={() => { window.location.href = `/e/${race.slug || race.uid}/register/`; }}>
                                Enter race
                            </PButton>
                        </CardBody>
                    </Card>
                )}

                {!race.registration_open && !hasResults && (
                    <Card>
                        <CardBody className="py-6 text-center text-default-500">
                            Entries for this race are closed.
                        </CardBody>
                    </Card>
                )}

                {/* Race-day check-in */}
                {!checkedIn ? (
                    <Card>
                        <CardBody className="space-y-3">
                            <div>
                                <h2 className="text-lg font-bold">Check in on race day</h2>
                                <p className="text-sm text-default-500">
                                    Let the organizer know you're here — enter the email you signed up with, or your race number.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <PInput className="flex-1" label="Email or race number"
                                        value={checkInValue} onChange={(e) => setCheckInValue(e.target.value)} />
                                <PButton className="sm:self-end" color="success" isLoading={busy} onPress={submitCheckIn}>
                                    Check me in
                                </PButton>
                            </div>
                        </CardBody>
                    </Card>
                ) : (
                    <Card>
                        <CardBody className="py-8 text-center">
                            <div className="text-3xl">✅</div>
                            <p className="mt-2 text-lg font-semibold">Checked in — good luck, {checkedIn.name}!</p>
                            {checkedIn.bib && <p className="text-sm text-default-500">Race number {checkedIn.bib}</p>}
                        </CardBody>
                    </Card>
                )}

                {/* Results, once they're in */}
                {hasResults && (
                    <Card>
                        <CardBody>
                            <h2 className="mb-3 text-lg font-bold">Results</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-default-200 text-left text-default-500">
                                            <th className="py-2 pr-2">#</th>
                                            <th className="py-2 pr-2">Name</th>
                                            <th className="py-2 pr-2">Category</th>
                                            <th className="py-2 pr-2">Time</th>
                                            <th className="py-2 text-right">Points</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((r: any) => (
                                            <tr key={r.uid} className="border-b border-default-100">
                                                <td className="py-2 pr-2 font-semibold">{r.overall_position ?? '-'}</td>
                                                <td className="py-2 pr-2">{r.name}{r.club ? <span className="text-default-400"> · {r.club}</span> : null}</td>
                                                <td className="py-2 pr-2 text-default-500">{r.category || '-'}</td>
                                                <td className="py-2 pr-2 tabular-nums">{r.gun_time || r.chip_time || '-'}</td>
                                                <td className="py-2 text-right font-semibold">{r._points}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                )}

                <p className="pt-2 text-center text-xs text-default-400">
                    Powered by <a href="https://scorepal.co" className="underline">ScorePal</a>
                </p>
            </div>
        </div>
    );
}
